import { type UseMutationResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useGlobalStore } from '../../store/global-store/global-store';
import {
  deleteFloraPage,
  getFloraPage,
  getFloraPages,
  getFloraPageViewUrl,
  updateFloraPage,
} from './flora-pages-client';
import type {
  FloraPage,
  FloraPageWritePayload,
  UseFloraPageResult,
  UseFloraPagesResult,
  UseFloraPageViewUrlResult,
} from './flora-pages-client.type';

// Deliberately NOT 'pages' — that key belongs to the dashboards feature
// (src/api/pages-client) and sharing it would cross-invalidate its cache.
const FLORA_PAGES_KEY = 'flora-pages';

/**
 * Hook to fetch the Flora pages visible to the current user in the active
 * organization.
 *
 * @returns {UseFloraPagesResult} Object containing the pages and the query
 */
const useFloraPages = (): UseFloraPagesResult => {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const query = useQuery({
    queryKey: [FLORA_PAGES_KEY, organizationId],
    queryFn: () => {
      if (!organizationId) throw new Error('Organization ID required');
      return getFloraPages(organizationId);
    },
    enabled: !!organizationId,
  });

  return { floraPages: query.data || [], query };
};

/**
 * Hook to fetch a single Flora page's metadata (title, visibility, owner) —
 * used by the page viewer to decide whether to show owner-only controls.
 *
 * @param {string | undefined} pageId - The page UUID, or undefined to skip
 * @returns {UseFloraPageResult} Object containing the page and the query
 */
const useFloraPage = (pageId: string | undefined): UseFloraPageResult => {
  const query = useQuery({
    queryKey: [FLORA_PAGES_KEY, pageId],
    queryFn: () => {
      if (!pageId) throw new Error('Page ID required');
      return getFloraPage(pageId);
    },
    enabled: !!pageId,
  });

  return { page: query.data, query };
};

/**
 * Hook to mint a short-lived signed URL for the viewer iframe. The token is
 * single-view and expires in minutes, so the result is never cached or
 * refetched on focus — remount (or refetch) to get a fresh URL.
 *
 * @param {string | undefined} pageId - The page UUID, or undefined to skip
 * @returns {UseFloraPageViewUrlResult} Object containing the view URL and the query
 */
const useFloraPageViewUrl = (pageId: string | undefined): UseFloraPageViewUrlResult => {
  const query = useQuery({
    queryKey: [FLORA_PAGES_KEY, pageId, 'view-url'],
    queryFn: () => {
      if (!pageId) throw new Error('Page ID required');
      return getFloraPageViewUrl(pageId);
    },
    enabled: !!pageId,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    // A view-url 404 (page deleted or un-shared) is deterministic, not
    // transient — retrying just spins the loading overlay for seconds before
    // the "Page unavailable" state can show. Fail fast to the error branch.
    retry: false,
  });

  return { viewUrl: query.data, query };
};

/**
 * Hook to update a page's title and/or visibility.
 *
 * Refetches the pages list and single-page metadata caches, but deliberately
 * spares the view-url query: that query is single-view and mounted live in
 * the viewer, so refetching it would mint a fresh signed URL and remount
 * the iframe — reloading the page the user is currently looking at just
 * because they changed its visibility.
 *
 * Uses refetchQueries rather than invalidateQueries because visibility is
 * toggled from inside the mounted viewer, where the pages-list query is
 * INACTIVE. invalidateQueries only refetches active queries, so it would
 * merely mark the inactive list stale — and the app's global
 * refetchOnMount:false means remounting the list on "Back to pages" would
 * not refetch it either, leaving a stale visibility badge for up to
 * staleTime. refetchQueries forces the refetch regardless of active state.
 *
 * @returns {UseMutationResult<FloraPage, Error, { id: string; payload: FloraPageWritePayload }>} The mutation
 */
const useUpdateFloraPage = (): UseMutationResult<FloraPage, Error, { id: string; payload: FloraPageWritePayload }> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => updateFloraPage(id, payload),
    onSuccess: () => {
      queryClient.refetchQueries({
        predicate: (query) => query.queryKey[0] === FLORA_PAGES_KEY && query.queryKey.at(-1) !== 'view-url',
      });
    },
  });
};

/**
 * Hook to delete a page.
 *
 * @returns {UseMutationResult<void, Error, string>} The mutation, keyed by page id
 */
const useDeleteFloraPage = (): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteFloraPage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FLORA_PAGES_KEY] });
    },
  });
};

export { FLORA_PAGES_KEY, useDeleteFloraPage, useFloraPage, useFloraPages, useFloraPageViewUrl, useUpdateFloraPage };
